// Per-URL cache entry — an in-flight or resolved <img> load. Kept
// private so the ImageLoaderManager stays the sole entry point;
// callers don't need to know about the loading state machine.
class _ImageCacheEntry {
    image: HTMLImageElement;
    imageUrl: string;
    private _loadedPromise: Promise<Error | HTMLImageElement>;
    private _resolve!: (value: HTMLImageElement) => void;
    private _reject!: (reason?: Error) => void;

    constructor(imageUrl: string) {
        this._loadedPromise = new Promise((resolve, reject) => {
            this._resolve = resolve;
            this._reject = reject;
        });
        this.imageUrl = imageUrl;
        this.image = new Image();
        this.image.onload = () => this._resolve(this.image);
        this.image.onerror = () => this._reject(new Error(`No imageProvided for ${imageUrl}`));
        this.image.crossOrigin = 'anonymous';
        this.image.src = imageUrl;
    }

    getImage(): Promise<Error | HTMLImageElement> {
        return this._loadedPromise;
    }
}

// Process-wide cache for image loads. Dedupes concurrent requests for
// the same URL — the second caller gets the first caller's in-flight
// promise instead of triggering a second HTTP GET.
class ImageLoaderManager {
    imagesContainer: _ImageCacheEntry[] = [];
    private static _singleton: ImageLoaderManager;

    static loadImage(imageUrl: string): Promise<Error | HTMLImageElement> {
        if (!imageUrl) {
            return Promise.reject(new Error('No image URL provided'));
        }

        const cached = ImageLoaderManager._instance.imagesContainer.find((image) => image.imageUrl === imageUrl);
        if (cached) {
            return cached.getImage();
        }
        const entry = new _ImageCacheEntry(imageUrl);
        ImageLoaderManager._instance.imagesContainer.push(entry);
        return entry.getImage();
    }

    static isImageLoaded(imageUrl: string): boolean {
        return ImageLoaderManager._instance.imagesContainer.some((image) => image.imageUrl === imageUrl);
    }

    private static get _instance(): ImageLoaderManager {
        if (!ImageLoaderManager._singleton) {
            ImageLoaderManager._singleton = new ImageLoaderManager();
        }
        return ImageLoaderManager._singleton;
    }
}

export { ImageLoaderManager };
